import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import SmartImage from './SmartImage';
import { resolveImageUrl as resolveImageUrlUtil } from '../utils/urlResolver';
import { resolveMuseumPeerDisplayName } from '../utils/museumActivityInsights';
import { coLearningWallBackdropProps } from '../ui/coLearningModalTheme';

const storyViewerBackdropClass =
  'fixed inset-0 z-[200100] flex items-center justify-center bg-[rgba(255,244,228,0.72)] p-3 backdrop-blur-3xl backdrop-saturate-150 md:p-6';

export default function ArtMuseumStoryViewer({ story, onClose }) {
  const [index, setIndex] = useState(0);
  const [resolvedArtistName, setResolvedArtistName] = useState('');
  const peers = story?.peers || [];
  const current = peers[index];
  const total = peers.length;

  const goNext = useCallback(() => {
    setIndex((i) => (i >= total - 1 ? 0 : i + 1));
  }, [total]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i <= 0 ? total - 1 : i - 1));
  }, [total]);

  useEffect(() => {
    setIndex(0);
  }, [story?.id]);

  useEffect(() => {
    let cancelled = false;
    setResolvedArtistName('');

    if (!current) return undefined;

    resolveMuseumPeerDisplayName(current).then((name) => {
      if (!cancelled) setResolvedArtistName(name || '');
    });

    return () => {
      cancelled = true;
    };
  }, [current?.id, current?.uid, current?.userName]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goNext, goPrev]);

  if (!story || !current) return null;

  const imageSrc = resolveImageUrlUtil(current.imageUrl) || current.imageUrl;
  const artworkSubtitle = (current.title || '').trim();
  const artistLabel =
    (resolvedArtistName || current.userName || '').trim() || 'View artist profile';
  const artistUid = current.uid || current.userId;

  const profilePath = artistUid ? `/artist-public-gallery/${artistUid}` : null;

  const handleArtistLinkClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  return createPortal(
    <div
      className={storyViewerBackdropClass}
      style={coLearningWallBackdropProps.style}
      role="dialog"
      aria-modal="true"
      aria-label={`Artist Salon — ${story.title}`}
      onClick={onClose}
    >
      <div
        className="relative flex h-[min(92vh,820px)] w-full max-w-lg flex-col overflow-hidden rounded-[28px] border border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9] via-[#fff8ef] to-[#fff6ec] shadow-[0_32px_90px_rgba(120,72,20,0.22)] ring-1 ring-white/70"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute left-0 right-0 top-0 z-20 flex gap-1.5 px-4 pt-4">
          {peers.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                i === index
                  ? 'bg-gradient-to-r from-[#d4a84b] to-[#c9a574]'
                  : 'bg-[#e8d4bc]/55'
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-[#e8d4bc] bg-white/95 text-[#5A4632] shadow-md transition hover:bg-white hover:text-[#2F251B]"
          aria-label="Close"
        >
          <X className="h-5 w-5" strokeWidth={2} />
        </button>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            className="relative flex min-h-0 flex-1 items-center justify-center bg-gradient-to-b from-[#faf6ef] via-[#f4efe6] to-[#efe8dc] pt-10 ring-1 ring-inset ring-[#e8d4bc]/40"
          >
            <button
              type="button"
              className="absolute inset-y-0 left-0 z-10 w-1/3"
              aria-label="Previous contribution"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 z-10 w-1/3"
              aria-label="Next contribution"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
            />
            {imageSrc ? (
              <SmartImage
                src={imageSrc}
                alt={current.title}
                className="max-h-full max-w-full object-contain drop-shadow-[0_8px_24px_rgba(90,60,30,0.12)]"
                containerClassName="flex h-full w-full items-center justify-center px-3 py-2"
              />
            ) : (
              <p className="text-sm font-medium text-[#8A7357]">No image</p>
            )}
          </div>

          <div
            className="relative z-20 shrink-0 border-t border-[#e8d4bc]/70 bg-gradient-to-b from-[#fffdf9]/98 to-[#fff6ec]/95 px-4 py-4 text-[#2F251B] shadow-[0_-12px_32px_rgba(120,72,20,0.06)]"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A7357]">
              {story.title}
            </p>
            {profilePath ? (
              <Link
                to={profilePath}
                onClick={handleArtistLinkClick}
                className="relative z-30 mt-1 block max-w-full cursor-pointer text-left font-serif text-lg font-semibold leading-snug text-[#92400e] underline decoration-[#d4a84b]/60 underline-offset-2 transition hover:text-[#78350f] hover:decoration-[#c9a574]"
                title="View this artist's public gallery"
              >
                {artistLabel}
              </Link>
            ) : (
              <p className="mt-1 font-serif text-lg font-semibold text-[#2F251B]">{artistLabel}</p>
            )}
            {artworkSubtitle ? (
              <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-[#5A4632]">{artworkSubtitle}</p>
            ) : null}
            <div className="mt-3 flex items-center justify-between text-xs font-medium text-[#8A7357]">
              <span>
                {index + 1} of {total} artist{total === 1 ? '' : 's'}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={goPrev}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e8d4bc] bg-white/90 text-[#5A4632] shadow-sm transition hover:border-[#d4b896] hover:bg-[#fffaf4] hover:text-[#2F251B]"
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e8d4bc] bg-white/90 text-[#5A4632] shadow-sm transition hover:border-[#d4b896] hover:bg-[#fffaf4] hover:text-[#2F251B]"
                  aria-label="Next"
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
